import { useEffect, useState } from 'react'
import type { Cluster } from './types'
import { finishesAtMs } from './activity'

const MAX_TIMER_DELAY_MS = 2 ** 31 - 1

/**
 * Re-renders the caller when the next of these incidents settles or finishes,
 * so a dot reading isInProgress turns grey at that moment and one pulsing on
 * isStillGrowing stops then.
 *
 * Nothing else is guaranteed to re-render then: the alerts dot used to wait for
 * whatever happened next — usually the 30s health poll — and read "in progress"
 * for up to half a minute after it was not.
 */
export function useFinishTick(clusters: Cluster[]): void {
  // Bumped by the timer; it is also what re-arms the effect for the next one.
  const [ticks, setTicks] = useState(0)

  useEffect(() => {
    const now = Date.now()
    let next = Infinity
    for (const c of clusters) {
      for (const at of [c.settled_at * 1000, finishesAtMs(c)]) {
        if (at > now && at < next) next = at
      }
    }
    if (!Number.isFinite(next)) return
    // Timers clamp any delay past 2^31-1 ms (~24.8 days) to 1ms, so a finish
    // further ahead — a camera whose clock is set years forward stamps one —
    // would fire, re-render and re-arm every millisecond. Capped, it re-checks
    // then instead.
    const t = setTimeout(() => setTicks((n) => n + 1), Math.min(next - now, MAX_TIMER_DELAY_MS))
    return () => clearTimeout(t)
  }, [clusters, ticks])
}
